import React, { Fragment } from 'react';
import PillGroup from '../interaction/PillGroup';
import Pill from '../interaction/Pill';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Divider,
  IconButton,
  Stack,
} from '@mui/material';
import {
  People as PeopleIcon,
  GroupAdd as GroupAddIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOff as DeactivateIcon,
  ToggleOn as ActivateIcon,
  Security as SecurityIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  Send as SendIcon,
  Warning as WarningIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

function RoleDisplay({ selectedRoles, roles }) {
  const selectedRoleObjects = roles.filter(role => selectedRoles.includes(role.id));
  if (selectedRoles.length === 0) return null;
  return (
    <Alert severity="info" icon={<SecurityIcon />} sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Selected roles:</Typography>
      <Box sx={{ mt: 1 }}>
        {selectedRoleObjects.map(role => (
          <Box key={role.id} sx={{ mb: 1 }}>
            <Typography variant="body2" fontWeight={500}>{role.name}</Typography>
            <Typography variant="caption" color="text.secondary">{role.description}</Typography>
          </Box>
        ))}
      </Box>
    </Alert>
  );
}

function UserCard({ user, onEdit, onDelete, onSendEmail, onToggleStatus }) {
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        p: 2,
        mb: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={1}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            <EmailIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
            <Typography variant="body2" fontWeight={600} noWrap>{user.email}</Typography>
          </Box>
          <Chip
            label={user.status || 'active'}
            color={user.status === 'active' ? 'success' : user.status === 'pending' ? 'warning' : 'default'}
            size="small"
            sx={{ flexShrink: 0, ml: 1 }}
          />
        </Box>

        {user.displayName && (
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">{user.displayName}</Typography>
          </Box>
        )}

        {user.company_name && (
          <Box display="flex" alignItems="center" gap={1}>
            <BusinessIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">{user.company_name}</Typography>
          </Box>
        )}

        {user.roles?.length > 0 && (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {[...user.roles].sort().map(role => (
              <Pill key={role} label={role} variant="light" size="xs" />
            ))}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary">
          Last login: {user.lastSignIn ? new Date(user.lastSignIn).toLocaleString() : 'Never'}
        </Typography>

        <Divider sx={{ my: 0.5 }} />

        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<EditIcon />}
            onClick={() => onEdit(user)}
            fullWidth
            sx={{ py: 1.2 }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<SendIcon />}
            onClick={() => onSendEmail(user)}
            fullWidth
            sx={{ py: 1.2 }}
          >
            Email
          </Button>
        </Box>

        <Box display="flex" gap={1}>
          {user.status !== 'pending' ? (
            <Button
              variant="outlined"
              size="medium"
              color={user.status === 'active' ? 'success' : 'warning'}
              startIcon={user.status === 'active' ? <DeactivateIcon /> : <ActivateIcon />}
              onClick={() => onToggleStatus(user)}
              fullWidth
              sx={{ py: 1.2 }}
            >
              {user.status === 'active' ? 'Deactivate' : 'Activate'}
            </Button>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
          <Button
            variant="outlined"
            color="error"
            size="medium"
            startIcon={<DeleteIcon />}
            onClick={() => onDelete(user)}
            fullWidth
            sx={{ py: 1.2 }}
          >
            Delete
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

function GuestCard({ user, onSetup, onDelete }) {
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'warning.light',
        p: 2,
        mb: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={1}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            <EmailIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
            <Typography variant="body2" fontWeight={600} noWrap>{user.email}</Typography>
          </Box>
          <Chip
            label={user.emailVerified ? 'Verified' : 'Unverified'}
            color={user.emailVerified ? 'success' : 'warning'}
            size="small"
            sx={{ flexShrink: 0, ml: 1 }}
          />
        </Box>

        {user.displayName && (
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">{user.displayName}</Typography>
          </Box>
        )}

        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon fontSize="small" color="warning" />
          <Chip label={user.reason} color="warning" size="small" />
        </Box>

        <Typography variant="caption" color="text.secondary">
          Created: {new Date(user.createdAt).toLocaleDateString()} · Last sign in: {user.lastSignIn ? new Date(user.lastSignIn).toLocaleString() : 'Never'}
        </Typography>

        <Divider sx={{ my: 0.5 }} />

        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            size="medium"
            startIcon={<EditIcon />}
            onClick={() => onSetup(user)}
            fullWidth
            sx={{ py: 1.2 }}
          >
            Complete Setup
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="medium"
            startIcon={<DeleteIcon />}
            onClick={() => onDelete(user)}
            fullWidth
            sx={{ py: 1.2 }}
          >
            Delete
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

export default function UserManagerMobile({
  companies,
  roles,
  filteredUsers,
  filteredUnassignedUsers,
  groupedUsers,
  groupBy,
  setGroupBy,
  GROUP_BY_OPTIONS,
  loading,
  error,
  setError,
  success,
  tabValue,
  setTabValue,
  searchQuery,
  setSearchQuery,
  openCreateDialog,
  createData,
  setCreateData,
  formErrors,
  setFormErrors,
  handleOpenCreateDialog,
  handleCloseCreateDialog,
  handleCreateUser,
  openEditDialog,
  selectedUser,
  editUserData,
  setEditUserData,
  handleEditUser,
  handleCloseEditDialog,
  handleUpdateUser,
  deleteDialog,
  handleDeleteClick,
  handleDeleteConfirm,
  handleDeleteCancel,
  handleSendWelcomeEmail,
  handleToggleUserStatus,
}) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  const tabs = [
    { label: 'Users', count: filteredUsers.length },
    { label: 'Guests', count: filteredUnassignedUsers.length },
  ];

  return (
    <Box sx={{ pb: 4 }}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Button
            variant="contained"
            startIcon={<GroupAddIcon />}
            onClick={handleOpenCreateDialog}
            fullWidth
            sx={{ mb: 1.5, py: 1.5 }}
          >
            Add User
          </Button>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by email, name, company, or role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }
            }}
          />
          <Box display="flex" alignItems="center" gap={1.5} mt={1.5}>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              Group by
            </Typography>
            <PillGroup
              options={GROUP_BY_OPTIONS}
              value={groupBy}
              onChange={setGroupBy}
            />
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Tab switcher */}
      <Box
        display="flex"
        mb={3}
        sx={{ borderRadius: 2, bgcolor: 'action.hover', p: 0.5, gap: 0.5 }}
      >
        {tabs.map((tab, i) => (
          <Button
            key={i}
            onClick={() => setTabValue(i)}
            fullWidth
            sx={{
              borderRadius: 1.5,
              py: 1,
              fontWeight: 600,
              fontSize: '0.875rem',
              bgcolor: tabValue === i ? 'background.paper' : 'transparent',
              color: tabValue === i ? 'primary.main' : 'text.secondary',
              boxShadow: tabValue === i ? 1 : 0,
              '&:hover': { bgcolor: tabValue === i ? 'background.paper' : 'action.selected' },
            }}
          >
            {tab.label}
            {tab.count > 0 && (
              <Box
                component="span"
                sx={{
                  ml: 0.75,
                  px: 0.75,
                  py: 0.1,
                  borderRadius: 10,
                  bgcolor: tabValue === i ? 'primary.main' : 'action.disabled',
                  color: tabValue === i ? 'primary.contrastText' : 'text.disabled',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  lineHeight: 1.6,
                }}
              >
                {tab.count}
              </Box>
            )}
          </Button>
        ))}
      </Box>

      {/* Users tab */}
      {tabValue === 0 && (
        <>
          {filteredUsers.length === 0 ? (
            <Box textAlign="center" py={6}>
              <PeopleIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                {searchQuery ? 'No users match your search.' : 'No users found.'}
              </Typography>
              {!searchQuery && (
                <Typography variant="body2" color="text.secondary">
                  Use the "Add User" button above to create your first user.
                </Typography>
              )}
            </Box>
          ) : (
            groupedUsers.map(({ label: groupLabel, users: groupUsers }) => (
              <Fragment key={groupLabel ?? '__all'}>
                {groupLabel && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 1,
                      mb: 0.75,
                      px: 0.5,
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: 'var(--sp-primary-dark)',
                    }}
                  >
                    {groupLabel}
                  </Typography>
                )}
                {groupUsers.map(user => (
                  <UserCard
                    key={user.id || user.uid}
                    user={user}
                    onEdit={handleEditUser}
                    onDelete={handleDeleteClick}
                    onSendEmail={handleSendWelcomeEmail}
                    onToggleStatus={handleToggleUserStatus}
                  />
                ))}
              </Fragment>
            ))
          )}
        </>
      )}

      {/* Guests tab */}
      {tabValue === 1 && (
        <>
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2">Authenticated users without assigned roles.</Typography>
          </Alert>
          {filteredUnassignedUsers.length === 0 ? (
            <Box textAlign="center" py={6}>
              <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                {searchQuery ? 'No guests match your search.' : 'No unassigned users found.'}
              </Typography>
              {!searchQuery && (
                <Typography variant="body2" color="text.secondary">
                  All authenticated users have been assigned roles!
                </Typography>
              )}
            </Box>
          ) : (
            filteredUnassignedUsers.map(user => (
              <GuestCard
                key={user.uid}
                user={user}
                onSetup={() => handleEditUser({
                  uid: user.uid,
                  email: user.email,
                  displayName: user.displayName,
                  ...user.firestoreData
                })}
                onDelete={() => handleDeleteClick({ uid: user.uid, email: user.email })}
              />
            ))
          )}
        </>
      )}

      {/* Create User Dialog — full screen on mobile */}
      <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog} fullScreen>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" fontWeight={700} component="span">Add User</Typography>
          <IconButton onClick={handleCloseCreateDialog} edge="end">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={0.5}>
            <TextField
              fullWidth
              label="Email Address"
              value={createData.email}
              onChange={(e) => {
                setCreateData({ ...createData, email: e.target.value });
                setFormErrors({ ...formErrors, email: '' });
              }}
              margin="normal"
              required
              error={!!formErrors.email}
              helperText={formErrors.email || 'User will sign in with Google/Microsoft using this email'}
            />
            <TextField
              fullWidth
              label="Display Name"
              value={createData.displayName}
              onChange={(e) => setCreateData({ ...createData, displayName: e.target.value })}
              margin="normal"
              helperText="Optional — will use email prefix if not provided"
            />
            <Autocomplete
              fullWidth
              options={companies}
              getOptionLabel={(option) => option.name || ''}
              value={companies.find(c => c.id === createData.company) || null}
              onChange={(event, newValue) => {
                setCreateData({ ...createData, company: newValue?.id || '' });
                setFormErrors({ ...formErrors, company: '' });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Company"
                  margin="normal"
                  required
                  error={!!formErrors.company}
                  helperText={formErrors.company || 'Select the company for this user'}
                  placeholder="Search and select company"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
            <Autocomplete
              multiple
              fullWidth
              options={[...roles].sort((a, b) => a.name.localeCompare(b.name))}
              getOptionLabel={(option) => option.name}
              value={roles.filter(r => createData.roles.includes(r.id))}
              onChange={(event, newValue) => {
                setCreateData({ ...createData, roles: newValue.map(r => r.id) });
                setFormErrors({ ...formErrors, roles: '' });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Roles"
                  margin="normal"
                  required
                  error={!!formErrors.roles}
                  helperText={formErrors.roles || 'Select at least one role'}
                  placeholder="Search and select roles"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <RoleDisplay selectedRoles={createData.roles} roles={roles} />
            <Alert severity="info" sx={{ mt: 2 }}>
              User will be created with <strong>pending</strong> status. Send them a welcome email so they know to sign in.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            fullWidth
            size="large"
            disabled={!createData.email.trim() || !createData.company || !createData.roles.length}
            sx={{ py: 1.5 }}
          >
            Create User
          </Button>
          <Button onClick={handleCloseCreateDialog} fullWidth size="large">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog — full screen on mobile */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} fullScreen>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" fontWeight={700} component="span" noWrap sx={{ flex: 1, mr: 1 }}>
            Edit User
          </Typography>
          <IconButton onClick={handleCloseEditDialog} edge="end">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={0.5}>
            {selectedUser?.email && (
              <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, mb: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">Editing user</Typography>
                <Typography variant="body1" fontWeight={600}>{selectedUser.email}</Typography>
              </Box>
            )}
            <TextField
              fullWidth
              label="Display Name"
              value={editUserData.displayName}
              onChange={(e) => setEditUserData({ ...editUserData, displayName: e.target.value })}
              margin="normal"
              helperText="User's display name"
            />
            <TextField
              fullWidth
              label="Email Address"
              value={editUserData.email}
              margin="normal"
              helperText="Only system admins can change email addresses"
              disabled
            />
            <Autocomplete
              fullWidth
              options={companies}
              getOptionLabel={(option) => option.name || ''}
              value={companies.find(c => c.id === editUserData.company) || null}
              onChange={(event, newValue) => setEditUserData({ ...editUserData, company: newValue?.id || '' })}
              renderInput={(params) => (
                <TextField {...params} label="Company" margin="normal" placeholder="Search and select company" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
            <Autocomplete
              multiple
              fullWidth
              options={[...roles].sort((a, b) => a.name.localeCompare(b.name))}
              getOptionLabel={(option) => option.name}
              value={roles.filter(r => editUserData.roles.includes(r.id))}
              onChange={(event, newValue) => setEditUserData({ ...editUserData, roles: newValue.map(r => r.id) })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Roles"
                  margin="normal"
                  placeholder="Search and select roles"
                  helperText="Select one or more roles"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <RoleDisplay selectedRoles={editUserData.roles} roles={roles} />
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={editUserData.status}
                onChange={(e) => setEditUserData({ ...editUserData, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
          <Button onClick={handleUpdateUser} variant="contained" fullWidth size="large" sx={{ py: 1.5 }}>
            Update User
          </Button>
          <Button onClick={handleCloseEditDialog} fullWidth size="large">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog — full screen on mobile */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel} fullScreen>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" fontWeight={700} component="span">Delete User</Typography>
          <IconButton onClick={handleDeleteCancel} edge="end">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, mb: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block">Deleting user</Typography>
            <Typography variant="body1" fontWeight={600}>{deleteDialog.userEmail}</Typography>
          </Box>
          <Alert
            severity="error"
            variant="outlined"
            sx={{ backgroundColor: 'error.lighter', '& .MuiAlert-icon': { color: 'error.main' } }}
          >
            This action cannot be undone. The user will be permanently deleted from the system.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            fullWidth
            size="large"
            sx={{ py: 1.5 }}
          >
            Delete User
          </Button>
          <Button onClick={handleDeleteCancel} fullWidth size="large">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
